import { z } from 'zod';

/** Контентные категории постов (без служебных all/news). */
export const FeedContentCategorySlug = z.enum([
  'events',
  'news',
  'flea_market',
  'services',
  'sport',
  'health',
  'rent',
  'bike_rent',
  'bike_sale',
  'car_rent',
  'jobs',
  'visa',
  'exchange',
  'lost_found',
  'other',
]);
export type FeedContentCategorySlug = z.infer<typeof FeedContentCategorySlug>;

/** Коммерческие категории — не показываются на вкладке «Новости». */
export const FEED_COMMERCIAL_CATEGORY_SLUGS = [
  'rent',
  'bike_rent',
  'bike_sale',
  'car_rent',
  'health',
  'services',
  'jobs',
  'visa',
  'exchange',
  'flea_market',
  'sport',
  'lost_found',
] as const satisfies readonly FeedContentCategorySlug[];

/** Категории, которые показываются на вкладке «Новости». */
export const FEED_NEWS_TAB_CATEGORY_SLUGS = [
  'news',
  'other',
] as const satisfies readonly FeedContentCategorySlug[];

/** Все контентные slug'и вкладок (для эксклюзивного SQL-фильтра). */
export const FEED_TAB_CATEGORY_SLUGS = FeedContentCategorySlug.options;

/** Slug'и, которые нельзя показывать вместе с activeTab на одной вкладке. */
export function feedTabPeerExcludeSlugs(activeTab: FeedContentCategorySlug): FeedContentCategorySlug[] {
  return FEED_TAB_CATEGORY_SLUGS.filter((s) => s !== activeTab);
}

/** Параметр category в GET /feed. */
export const FeedCategoryParam = z.union([z.literal('all'), FeedContentCategorySlug]);
export type FeedCategoryParam = z.infer<typeof FeedCategoryParam>;

export interface FeedCategoryDefinition {
  label: string;
  order: number;
  hashtags: readonly string[];
  keywords: readonly string[];
}

export const FEED_CATEGORY_DEFINITIONS: Record<FeedContentCategorySlug, FeedCategoryDefinition> = {
  events: {
    label: 'Афиша',
    order: 2,
    hashtags: ['афиша', 'afisha', 'events', 'ивент', 'мероприятие', 'концерт'],
    keywords: ['афиша', 'мероприятие', 'концерт', 'вечеринка', 'событие'],
  },
  news: {
    label: 'Новости',
    order: 3,
    hashtags: ['новости', 'news', 'сводка'],
    keywords: ['новости', 'сводка', 'важно', 'объявление админ'],
  },
  flea_market: {
    label: 'Барахолка',
    order: 4,
    hashtags: ['барахолка', 'продам', 'куплю', 'market'],
    keywords: ['продам', 'куплю', 'барахолка', 'обмен', 'цена', 'состояние'],
  },
  services: {
    label: 'Услуги',
    order: 5,
    hashtags: ['услуги', 'services', 'мастер'],
    keywords: ['услуги', 'ремонт', 'мастер', 'клининг', 'доставка', 'консультация'],
  },
  sport: {
    label: 'Спорт',
    order: 6,
    hashtags: ['спорт', 'sport', 'футбол'],
    keywords: ['спорт', 'тренировка', 'футбол', 'зал', 'йога', 'марафон', 'бассейн'],
  },
  health: {
    label: 'Здоровье',
    order: 7,
    hashtags: ['здоровье', 'health', 'врач'],
    keywords: ['врач', 'клиника', 'здоровье', 'стоматолог', 'аптека', 'массаж'],
  },
  rent: {
    label: 'Аренда',
    order: 8,
    hashtags: ['аренда', 'rent', 'снять'],
    keywords: ['аренда', 'сдам', 'сниму', 'квартира', 'жильё', 'посуточно'],
  },
  bike_rent: {
    label: 'Аренда байков',
    order: 9,
    hashtags: ['байк', 'мотоаренда', 'скутер'],
    keywords: ['аренда байк', 'аренда скутер', 'мото аренда', 'байк напрокат'],
  },
  bike_sale: {
    label: 'Продажа байков',
    order: 10,
    hashtags: ['продажабайк', 'мото'],
    keywords: ['продам байк', 'продам скутер', 'мотоцикл', 'honda', 'yamaha'],
  },
  car_rent: {
    label: 'Аренда машин',
    order: 11,
    hashtags: ['автоаренда', 'машина'],
    keywords: ['аренда авто', 'аренда машин', 'прокат авто', 'такси долгосрочно'],
  },
  jobs: {
    label: 'Работа',
    order: 12,
    hashtags: ['работа', 'job', 'вакансия'],
    keywords: ['работа', 'вакансия', 'ищу работу', 'резюме', 'зарплата'],
  },
  visa: {
    label: 'Виза',
    order: 13,
    hashtags: ['виза', 'visa'],
    keywords: ['виза', 'внж', 'продление визы', 'иммиграция'],
  },
  exchange: {
    label: 'Обмен',
    order: 14,
    hashtags: ['обмен', 'exchange', 'валюта'],
    keywords: ['обмен валют', 'курс', 'usd', 'vnd', 'перевод денег'],
  },
  lost_found: {
    label: 'Потеряно',
    order: 15,
    hashtags: ['потеряно', 'найдено', 'lost'],
    keywords: ['потерял', 'нашёл', 'найден', 'потерян', 'утерян'],
  },
  other: {
    label: 'Другое',
    order: 999,
    hashtags: [],
    keywords: [],
  },
};

export const FeedCategoryTabDto = z.object({
  slug: FeedCategoryParam,
  label: z.string(),
});
export type FeedCategoryTabDto = z.infer<typeof FeedCategoryTabDto>;

export const FeedCategoriesResponse = z.object({
  tabs: z.array(FeedCategoryTabDto),
});
export type FeedCategoriesResponse = z.infer<typeof FeedCategoriesResponse>;

/** Порядок L2: news первая, остальные по order, all последняя. */
export function buildFeedUiTabs(): FeedCategoryTabDto[] {
  const content = (
    Object.entries(FEED_CATEGORY_DEFINITIONS) as [FeedContentCategorySlug, FeedCategoryDefinition][]
  )
    .filter(([slug]) => slug !== 'news')
    .sort((a, b) => a[1].order - b[1].order || a[0].localeCompare(b[0]))
    .map(([slug, def]) => ({ slug, label: def.label }));

  return [
    { slug: 'news', label: FEED_CATEGORY_DEFINITIONS.news.label },
    ...content,
    { slug: 'all', label: 'Все' },
  ];
}
